import { IChatBlock, IChatMessage, IConfirmRequest, ISyncedEntity, TProposalStatus } from "../types";

const SYNCED_ENTITY_LABEL_KEYS: Record<string, string> = {
  vendors: "synced_entity_vendors",
  customers: "synced_entity_customers",
  invoices: "synced_entity_invoices",
  bills: "synced_entity_bills",
  products: "synced_entity_products",
  purchase_orders: "synced_entity_purchase_orders",
  purchaseOrders: "synced_entity_purchase_orders",
  purchase_order: "synced_entity_purchase_orders",
};

export const extractSyncedEntities = (
  data: any,
  translate: (text: string, options?: any) => string,
): ISyncedEntity[] => {
  const counts = data?.last_sync?.counts;

  if (counts && typeof counts === "object") {
    return Object.entries(counts)
      .filter(([, count]) => typeof count === "number")
      .map(([key, count]) => ({
        key,
        label: translate(SYNCED_ENTITY_LABEL_KEYS[key] ?? key),
        count: count as number,
      }));
  }

  const raw =
    data?.synced_entities ||
    data?.syncedEntities ||
    data?.synced ||
    data?.entities;

  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .map((entity: any) => {
        const label =
          typeof entity === "string" ? entity : entity?.name || entity?.label;
        return { key: label, label, count: entity?.count ?? 0 };
      })
      .filter((entity) => Boolean(entity.label));
  }

  return [];
};

export const extractSyncErrors = (data: any): string[] => {
  const errors = data?.last_sync?.errors;
  if (errors && typeof errors === "object") {
    return Object.values(errors).map((err) => String(err));
  }
  return [];
};

const APPROVED_STATES = new Set(["approved", "approve", "executed", "completed"]);
const REJECTED_STATES = new Set(["rejected", "reject", "declined"]);
const FAILED_STATES = new Set(["failed", "error", "failure"]);
const EXPIRED_STATES = new Set([
  "expired",
  "never_decided",
  "never-decided",
  "neverdecided",
  "timed_out",
  "timeout",
]);

export const normalizeProposalStatus = (state: unknown): TProposalStatus => {
  const s = typeof state === "string" ? state.toLowerCase() : "";
  if (APPROVED_STATES.has(s)) return "approved";
  if (REJECTED_STATES.has(s)) return "rejected";
  if (FAILED_STATES.has(s)) return "failed";
  if (EXPIRED_STATES.has(s)) return "expired";
  return "pending";
};

export const buildResolutionFromState = (
  state: unknown,
  extra: { summary?: string; results?: IConfirmRequest["results"] } = {},
): IConfirmRequest | undefined => {
  const status = normalizeProposalStatus(state);
  if (status === "pending") return undefined;

  return {
    ok: status === "approved",
    executed: status === "approved",
    decision: status === "rejected" ? "reject" : "approve",
    status,
    summary: extra.summary || "",
    results: extra.results || [],
    is_error: status === "failed",
  };
};

let historyMessageIdCounter = 0;
const nextHistoryMessageId = () =>
  `history-message-${++historyMessageIdCounter}`;

let historyBlockIdCounter = 0;
const nextHistoryBlockId = () => `history-block-${++historyBlockIdCounter}`;

export const mapConversationHistoryToChatMessages = (
  rawMessages: any[],
  translate: (text: string, options?: any) => string,
): IChatMessage[] =>
  (rawMessages || []).map((msg: any): IChatMessage => {
    const blocks: IChatBlock[] = [];

    if (msg.trace && Array.isArray(msg.trace)) {
      msg.trace.forEach((t: any) => {
        if (t.kind === "think") {
          const existingThinkingBlock = blocks.find(
            (b) => b.type === "thinking",
          );
          if (existingThinkingBlock) {
            existingThinkingBlock.content += "\n\n" + (t.text || "");
          } else {
            blocks.push({
              id: nextHistoryBlockId(),
              type: "thinking",
              content: t.text || "",
            });
          }
        } else if (t.kind === "tool") {
          blocks.push({
            id: nextHistoryBlockId(),
            type: "tool",
            toolName: t.name,
            toolStatus: t.state === "error" ? "error" : "done",
            toolIsError: t.state === "error",
          });
        } else if (t.kind === "code") {
          blocks.push({
            id: nextHistoryBlockId(),
            type: "code",
            code: {
              summary: translate("execution_complete"),
              meta: t.tool || translate("sandbox_fallback"),
              lines: t.code ? t.code.split("\n") : [],
              stdout: t.out ? t.out.text : undefined,
              is_error: t.out ? t.out.isError : false,
            },
          });
        }
      });
    }

    if (msg.blocks && Array.isArray(msg.blocks)) {
      msg.blocks.forEach((b: any) => {
        if (b.t === "text" || b.type === "text") {
          blocks.push({
            id: b.id || nextHistoryBlockId(),
            type: "text",
            content: b.text ?? b.content ?? "",
          });
        } else if ((b.t === "artifact" || b.type === "artifact") && b.a) {
          if (b.a.t === "table") {
            blocks.push({
              id: b.id || nextHistoryBlockId(),
              type: "table",
              title: b.a.title,
              columns: b.a.columns,
              rows: b.a.rows,
              toolStatus: "done",
            });
          } else if (b.a.t === "file") {
            blocks.push({
              id: b.id || nextHistoryBlockId(),
              type: "file",
              fileId: b.a.handle,
              fileName: b.a.name,
              fileSize: b.a.size,
            });
          } else if (b.a.t === "chart") {
            blocks.push({
              id: b.id || nextHistoryBlockId(),
              type: "chart",
              spec: b.a.spec,
            });
          } else if (b.a.t === "write") {
            blocks.push({
              id: b.id || nextHistoryBlockId(),
              type: "confirm_request",
              confirmTool: b.a.tool,
              confirmSummary: b.a.summary,
              confirmDetails: b.a.details,
              write_id: b.a.write_id,
              rules: b.a.rules,
              confirmResolution: buildResolutionFromState(b.a.state, {
                summary: b.a.note,
                results: b.a.results,
              }),
            });
          }
        }
      });
    }

    if (msg.sources && Array.isArray(msg.sources)) {
      const lastTextBlock = blocks
        .slice()
        .reverse()
        .find((b) => b.type === "text");
      if (lastTextBlock) {
        lastTextBlock.referencedDocs = msg.sources;
      }
    }

    return {
      id: msg.id || nextHistoryMessageId(),
      sender: msg.role === "assistant" ? "ai" : "user",
      content: msg.text ?? msg.content ?? msg.message ?? "",
      blocks,
      attachments: msg.attachments || [],
    };
  });

export function findLastToolBlockIndex(
  blocks: IChatBlock[],
  toolName: string | undefined,
): number {
  for (let i = blocks.length - 1; i >= 0; i--) {
    if (blocks[i].type === "tool" && blocks[i].toolName === toolName) {
      return i;
    }
  }
  return -1;
}

export const copyTextToClipboard = (message: IChatMessage) =>
  message?.blocks && message?.blocks?.length > 0
    ? message?.blocks
        .filter((block) => block.type === "text")
        .map((filteredBlock) => filteredBlock.content)
        .join("")
    : "";
